import { adminModeStore, useAdminMode } from '@apps/web/lib/admin_mode_store';
import { getApiClient } from '@apps/web/lib/api_client';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type AdminAgentNoteListResponse,
    type AdminAgentNoteRead,
    type AdminAgentNoteReclassifyResponse,
    type AdminAgentNoteRetryResponse,
    type AdminAgentNoteStatsResponse,
    type ApiQueryParamsType,
    type components,
} from '@libraries/typescript/api_client';

export function useAdminAgentNotes(
    params: {
        readonly page?: number;
        readonly perPage?: number;
        readonly organizationId?: string;
        readonly status?: components['schemas']['AgentNoteStatus'];
        readonly source?: components['schemas']['AgentNoteSource'];
        readonly domain?: components['schemas']['AgentNoteDomain'];
        readonly parked?: boolean;
        readonly parkItems?: boolean;
        readonly zeroItems?: boolean;
        readonly search?: string;
        readonly createdAfter?: string;
        readonly createdBefore?: string;
    } = {},
    options: {
        readonly keepPreviousData?: boolean;
        readonly refreshIntervalMs?: number;
    } = {},
) {
    // Drive requests off the admin panel's own mode toggle (the sibling
    // admin hooks' convention) instead of the customer app's persisted mode.
    const adminMode = useAdminMode();
    const queryParams = buildAdminAgentNotesQueryParams(params);

    return useApiQuery<
        '/admin/agent-notes',
        'get',
        AdminAgentNoteListResponse,
        AdminAgentNoteListResponse
    >({
        path: '/admin/agent-notes',
        method: 'get',
        queryParams,
        mode: adminMode,
        select: (response) => response,
        keepPreviousData: options.keepPreviousData,
        refreshInterval: options.refreshIntervalMs,
    });
}

export function buildAdminAgentNotesQueryParams(params: {
    readonly page?: number;
    readonly perPage?: number;
    readonly organizationId?: string;
    readonly status?: components['schemas']['AgentNoteStatus'];
    readonly source?: components['schemas']['AgentNoteSource'];
    readonly domain?: components['schemas']['AgentNoteDomain'];
    readonly parked?: boolean;
    readonly parkItems?: boolean;
    readonly zeroItems?: boolean;
    readonly search?: string;
    readonly createdAfter?: string;
    readonly createdBefore?: string;
}): ApiQueryParamsType<'/admin/agent-notes', 'get'> | undefined {
    type AgentNotesQueryParams = ApiQueryParamsType<
        '/admin/agent-notes',
        'get'
    >;
    const candidate: AgentNotesQueryParams = {
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.perPage !== undefined ? { per_page: params.perPage } : {}),
        ...(params.organizationId !== undefined
            ? { organization_id: params.organizationId }
            : {}),
        ...(params.status !== undefined ? { status: params.status } : {}),
        ...(params.source !== undefined ? { source: params.source } : {}),
        ...(params.domain !== undefined ? { domain: params.domain } : {}),
        ...(params.parked !== undefined ? { parked: params.parked } : {}),
        ...(params.parkItems !== undefined
            ? { park_items: params.parkItems }
            : {}),
        ...(params.zeroItems !== undefined
            ? { zero_items: params.zeroItems }
            : {}),
        ...(params.search !== undefined && params.search !== ''
            ? { search: params.search }
            : {}),
        ...(params.createdAfter !== undefined
            ? { created_after: params.createdAfter }
            : {}),
        ...(params.createdBefore !== undefined
            ? { created_before: params.createdBefore }
            : {}),
    };
    return Object.keys(candidate).length > 0 ? candidate : undefined;
}

export function useAdminAgentNoteStats(
    params: {
        readonly organizationId?: string;
        readonly startDate?: string;
        readonly endDate?: string;
    } = {},
    options: { readonly keepPreviousData?: boolean } = {},
) {
    const adminMode = useAdminMode();
    type AgentNoteStatsQueryParams = ApiQueryParamsType<
        '/admin/agent-notes/stats',
        'get'
    >;

    const candidate: AgentNoteStatsQueryParams = {
        ...(params.organizationId !== undefined
            ? { organization_id: params.organizationId }
            : {}),
        ...(params.startDate !== undefined
            ? { start_date: params.startDate }
            : {}),
        ...(params.endDate !== undefined ? { end_date: params.endDate } : {}),
    };
    const queryParams =
        Object.keys(candidate).length > 0 ? candidate : undefined;

    return useApiQuery<
        '/admin/agent-notes/stats',
        'get',
        AdminAgentNoteStatsResponse,
        AdminAgentNoteStatsResponse
    >({
        path: '/admin/agent-notes/stats',
        method: 'get',
        queryParams,
        mode: adminMode,
        select: (response) => response,
        keepPreviousData: options.keepPreviousData,
    });
}

export function useAdminAgentNote(
    noteId: string | null,
    options: { readonly refreshIntervalMs?: number } = {},
) {
    const adminMode = useAdminMode();
    return useApiQuery<
        '/admin/agent-notes/{note_id}',
        'get',
        AdminAgentNoteRead,
        AdminAgentNoteRead
    >({
        path: '/admin/agent-notes/{note_id}',
        method: 'get',
        pathParams: noteId !== null ? { note_id: noteId } : undefined,
        mode: adminMode,
        enabled: noteId !== null,
        select: (response) => response,
        refreshInterval: options.refreshIntervalMs,
    });
}

export async function retryAgentNoteClassification(
    noteId: string,
    content?: string,
): Promise<AdminAgentNoteRetryResponse> {
    return getApiClient().post({
        url: '/admin/agent-notes/{note_id}/retry-classification',
        pathParams: { note_id: noteId },
        body: content === undefined ? undefined : { content },
        mode: adminModeStore.getMode(),
    });
}

export async function reclassifyAgentNotes(
    noteIds: Array<string>,
): Promise<AdminAgentNoteReclassifyResponse> {
    return getApiClient().post({
        url: '/admin/agent-notes/reclassify',
        body: { note_ids: noteIds },
        mode: adminModeStore.getMode(),
    });
}
